import React, { useState, useMemo, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { CircularProgress, Backdrop, Button } from "@material-ui/core";
import { toast } from "react-toastify";
import { CloudUpload } from "@material-ui/icons";

import { importarArquivos, getPlanilhaCNIS, getPlanilhaFontesPagadoras } from "../../../../Store/Actions/API/importacaoActions";
import { styleDropzone, styleBackdrop } from "./styles";

export default function Upload({ cpf }) {

  const realizandoImportacao = useRef(false);
  const [importandoArquivos, setImportandoArquivos] = useState(false);
  const [porcentagem, setPorcentagem] = useState(0);
  const [mensagemErro, setMensagemErro] = useState(null);
  const [visibleBackdrop, setVisibleBackdrop] = useState(false);
  const dropzone = styleDropzone();
  const backdrop = styleBackdrop();

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: [".zip", ".rar", ".7z", ".pdf", ".xlsx"],
    disabled: importandoArquivos,
    onDrop
  });

  const style = useMemo(() => {
    if (isDragActive) return `${dropzone.base} ${dropzone.activeStyle}`;
    if (isDragAccept) return `${dropzone.base} ${dropzone.acceptStyle}`;
    if (isDragReject) return `${dropzone.base} ${dropzone.rejectStyle}`;
    return dropzone.base;
  }, [isDragActive, isDragAccept, isDragReject]); // eslint-disable-line

  const validaExtensaoArquivos = (acceptedFiles) => {
    const tamanhoLimiteDoArquivo = 2000000;
    const extensaoZipadasValidas = [".7z", ".rar", ".zip"];
    const extensaoArquivosValidos = ['.pdf','.xlsx'];

    const validador = extensaoArquivosValidos
      .join("|")
      .concat(`|${extensaoZipadasValidas.join("|")}`);

    let existeArquivoExtensao = false;

    acceptedFiles.find((file) => {
      const resultado = RegExp(validador, "g").exec(file.name);

      if (resultado && resultado.length <= tamanhoLimiteDoArquivo) {
        existeArquivoExtensao = true;
      } else {
        existeArquivoExtensao = false;
      }
      return file;
    });

    return existeArquivoExtensao;
  }

  async function onDrop(acceptedFiles) {
    lidarInicioImportacao();

    if (acceptedFiles && acceptedFiles.length > 20) {
      setMensagemErro("Acima de 20 arquivos, crie um arquivo .zip ou .rar para uma melhor experiência");
      toast.error("Acima de 20 arquivos, crie um arquivo .zip ou .rar para uma melhor experiência");
      lidarFimImportacao();
    } else {
      // valida as extensoes e tamanho do arquivo
      if (validaExtensaoArquivos(acceptedFiles)) {
        realizarImportacao(acceptedFiles);
      } else {
        setMensagemErro("Arquivo inválido. Por favor, verifique a extensão dos arquivos carregados.");
        toast.error('Arquivo inválido. Por favor, verifique a extensão dos arquivos carregados.');
        lidarFimImportacao();
      }
    }
  }

  const realizarImportacao = async (files = []) => {
    await importarArquivos(cpf, files, (progressEvent) => {
      const percent = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
      lidarProgressoArquivo(percent);
    });

    lidarFimImportacao();
  }

  const lidarInicioImportacao = () => {
    setImportandoArquivos(true);
    realizandoImportacao.current = true;
    setVisibleBackdrop(true);
    setMensagemErro("");
  }

  const lidarProgressoArquivo = (value) => {
    setPorcentagem(value);
    if (value === 100) {
      setVisibleBackdrop(false);
    }
  }

  const lidarFimImportacao = () => {
    setImportandoArquivos(false);
    realizandoImportacao.current = false;
    setVisibleBackdrop(false);
    setPorcentagem(0);
  }

  function renderDropContent() {
    if (importandoArquivos && !visibleBackdrop) {
      return (
        <>
          <CircularProgress className={dropzone.icon} disableShrink />
          <span className={dropzone.text}>
            Aguarde a importação finalizar...
          </span>
        </>
      );
    }
    return (
      <>
        <CloudUpload className={dropzone.icon} />
        <span className={dropzone.text}>
          Solte ou clique para enviar arquivos
          <small>Extensões permitidas: PDF, ZIP, RAR ou 7Z</small>
          <small>(tamanho máximo de 2GB)</small>
          <small className={dropzone.error}>{mensagemErro}</small>
        </span>
        <Button
          id="btn-planilha-cnis"
          className={dropzone.buttonSecondary}
          style={{color : "#40a9ff"}}
          onClick={async (e) => {
            e.stopPropagation();
            await getPlanilhaCNIS(cpf);
          }}
        >
          Baixar a planilha CNIS
        </Button>
        <Button
          id="btn-planilha-fontesPagadoras"
          className={dropzone.buttonSecondary}
          style={{color : "#40a9ff"}}
          onClick={async (e) => {
            e.stopPropagation();
            await getPlanilhaFontesPagadoras(cpf);
          }}
        >
          Baixar a planilha de fontes pagadoras
        </Button>
      </>
    );
  }

  return (
    <>
      <Backdrop className={backdrop.base} open={visibleBackdrop}>
        <CircularProgress
          className={backdrop.icon}
          variant="determinate"
          color="secondary"
          size={60}
          value={porcentagem}
        />
        <span className={backdrop.value}>{porcentagem}%</span>
        <span className={backdrop.text}>Enviando arquivo(s)</span>
        <span className={backdrop.text2}>Por favor, não feche a aba...</span>
      </Backdrop>
      <div {...getRootProps({ className: style })}>
        <input {...getInputProps()} />
        {renderDropContent()}
      </div>
    </>
  );
};
